/**
 * One release, after the fact: what it contained, what was deployed or skipped and
 * why, and its announcement as posted. Addressed by version so it can be linked from
 * Slack or a bug report.
 *
 * Read-only, and not a second copy of the deploying view. The actions live on the home
 * page, which shows the release that is actually open; this page has to keep working
 * for a release closed months ago, when nothing but its own document and its
 * components' history remains. `/releases/:version/changes` is the same page with the
 * change set expanded, so a link can point straight at the diff.
 */
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert,
    Card,
    CardContent,
    Chip,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { compareUrl, componentReleaseSummary, untaggedVersion } from '@finvari/deployer';
import { useComponents, useNow, useRelease, CAPTION_TICK_MS } from '../hooks';
import { componentRows } from '../lib/componentRows';
import { agoTextCoarse } from '../lib/hostStatus';
import {
    changedComponents,
    deployOutcomeText,
    pastTagText,
    shippedRef,
    skippedComponents,
    SHORT_SHA,
} from '../lib/releaseSummary';
import { AnnouncementPreview } from '../components/AnnouncementPreview';
import { ChangeSetView } from '../components/ChangeSetView';
import { FactList, type Fact } from '../components/FactList';

export function ReleasePage({ showChanges = false }: { showChanges?: boolean }) {
    const { version } = useParams();
    const { value: release, loading } = useRelease(version ?? '-');
    const components = useComponents();
    const now = useNow(CAPTION_TICK_MS);

    if (loading) {
        return <Typography color="text.secondary">Loading…</Typography>;
    }
    if (!release) {
        return (
            <Alert severity="warning">
                No release {version} has been deployed through this system. Releases from before the deploy half moved
                here have a tag but no document.
            </Alert>
        );
    }

    // The component documents carry only the *latest* state, so for a closed release
    // the marker columns describe the checkout as it is now, not as it was. What this
    // page states about the release itself comes from the release document; the rows
    // are used for the per-component acknowledgement and deploy attribution it holds.
    const rows = componentRows({
        version: release.version,
        plan: release.plan,
        components: components.value,
        acknowledgements: release.acknowledgements,
        instructions: [],
    });
    const changed = changedComponents(release.plan);
    const skipped = skippedComponents(release.plan, release.acknowledgements);
    const closed = release.closedAt?.toMillis?.();
    const opened = release.openedAt?.toMillis?.();

    // What shipped, which is the branch tip and not always the tag; the tag's own
    // commit is named in the phrase beside it when the two differ.
    const pastTag = pastTagText(release);
    const facts: Fact[] = [
        { label: 'Tag', value: release.releaseTag },
        { label: 'Baseline', value: release.baselineTag },
        {
            label: 'Deployed commit',
            value: (release.deployCommit ?? release.releaseCommit).slice(0, SHORT_SHA) + (pastTag ? ` · ${pastTag}` : ''),
        },
        { label: 'Opened', value: opened === undefined ? '—' : agoTextCoarse(Math.max(0, now - opened)) },
        {
            label: 'Closed',
            value: closed === undefined ? 'still open' : agoTextCoarse(Math.max(0, now - closed)),
        },
        {
            label: 'Announced',
            value: release.announcement?.postedAt ? `posted by ${release.announcement.postedBy?.email ?? 'unknown'}` : 'not posted',
        },
        { label: 'Merge-back', value: release.mergeBackObserved ? 'observed on origin/main' : 'not observed' },
        { label: 'Knowledge base', value: release.knowledgePublished ? 'published' : 'not published' },
        { label: 'Factory artifacts', value: release.factoryArtifactsPublished ? 'published' : 'not published' },
        {
            label: 'Compare',
            value: (
                <Link href={compareUrl(release.baselineTag, shippedRef(release))} target="_blank" rel="noreferrer">
                    {release.baselineTag} → {shippedRef(release).slice(0, SHORT_SHA)}
                </Link>
            ),
        },
    ];
    if (release.closedBy) facts.push({ label: 'Closed by', value: release.closedBy.email });
    // Only present when someone closed through a blocker, and the reason they gave is
    // the answer to why the release looks the way it does.
    if (release.closeReason) facts.push({ label: 'Closed despite', value: release.closeReason });

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5" component="h1">
                    {release.version}
                </Typography>
                <Chip
                    size="small"
                    color={release.phase === 'closed' ? 'default' : 'primary'}
                    label={release.phase}
                />
                <Link component={RouterLink} to="/releases" variant="body2">
                    All releases
                </Link>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <FactList facts={facts} />
                </CardContent>
            </Card>

            {/*
              * On the permanent record, above the component table, because it is the
              * part of a release nothing in this system did: the code is in the tag,
              * and whether the projects, service accounts and IAM bindings it assumes
              * exist rests entirely on what the operator wrote here. Somebody reading a
              * closed release to work out why production behaves as it does needs that
              * sentence more than they need the component list.
              */}
            {(release.infraChanges ?? []).length > 0 && (
                <Alert severity="info">
                    <Stack spacing={0.5}>
                        <Typography variant="body2">
                            {release.infraChanges!.length === 1
                                ? 'This release changed a Pulumi program'
                                : `This release changed ${release.infraChanges!.length} Pulumi programs`}
                            . Nothing it deployed applied them — an owner runs <code>pulumi up</code> — so what
                            happened is whatever was recorded at the close.
                        </Typography>
                        {release.infraChanges!.map((change) => {
                            const ack = release.infraAcknowledgements?.[change.program];
                            return (
                                <Typography key={change.program} variant="body2">
                                    <strong>{change.program}</strong>:{' '}
                                    {ack ? `${ack.reason} — ${ack.by.email}` : 'nothing recorded yet.'}
                                </Typography>
                            );
                        })}
                    </Stack>
                </Alert>
            )}

            {skipped.length > 0 && (
                <Alert severity="warning">
                    <Stack spacing={0.5}>
                        <Typography variant="body2">
                            {skipped.length === 1 ? 'One component' : `${skipped.length} components`} this release
                            changed {skipped.length === 1 ? 'was' : 'were'} not deployed. That code stayed out of
                            production until a later release carried it.
                        </Typography>
                        {skipped.map((s) => (
                            <Typography key={s.name} variant="body2">
                                <strong>{s.name}</strong>: {s.acknowledgement.reason} — {s.acknowledgement.by.email}
                            </Typography>
                        ))}
                    </Stack>
                </Alert>
            )}

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Components changed
                    </Typography>
                    {changed.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            This release changed no deployable component.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Component</TableCell>
                                    <TableCell>Change</TableCell>
                                    <TableCell>Deployed for this release</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {changed.map((name) => {
                                    const row = rows.find((r) => r.name === name);
                                    return (
                                        <TableRow key={name}>
                                            <TableCell>{name}</TableCell>
                                            <TableCell>{release.plan[name]?.changeKind}</TableCell>
                                            <TableCell>
                                                {row?.deployedBy
                                                    ? `${row.deployedBy.by.email}`
                                                    : deployOutcomeText(
                                                          release.plan[name],
                                                          release.acknowledgements?.[name],
                                                      )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {release.announcement?.json && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Announcement
                        </Typography>
                        <AnnouncementPreview json={release.announcement.json} />
                    </CardContent>
                </Card>
            )}

            {/*
              * A connector build or app version this release also carried, each
              * with the announcement that went out for it. Part of the record for
              * the same reason the server's is: "when did connector 1.2.735 ship,
              * and what were people told about it" is a question asked months
              * later, and this is the only page that can answer it.
              */}
            {(release.componentReleases ?? []).map((componentRelease) => (
                <Card key={`${componentRelease.component}/${componentRelease.version}`} variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6">
                                {componentRelease.component === 'app' ? 'Phone app' : 'Connector'}{' '}
                                {untaggedVersion(componentRelease.version)}
                            </Typography>
                            <Chip
                                size="small"
                                color={componentRelease.announcement?.postedAt ? 'success' : 'default'}
                                variant={componentRelease.announcement?.postedAt ? 'filled' : 'outlined'}
                                label={componentRelease.announcement?.postedAt ? 'announced' : 'not announced'}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {componentReleaseSummary(componentRelease)}
                        </Typography>
                        {componentRelease.announcement?.json && (
                            <AnnouncementPreview json={componentRelease.announcement.json} />
                        )}
                    </CardContent>
                </Card>
            ))}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1 }}>
                        <Typography variant="h6">What shipped</Typography>
                        {!showChanges && (
                            <Link component={RouterLink} to={`/releases/${release.version}/changes`} variant="body2">
                                Open the file-by-file diff
                            </Link>
                        )}
                    </Stack>
                    {showChanges ? (
                        <ChangeSetView pointer={release.changes} />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {release.changes
                                ? `${release.changes.fileCount} files changed between ${release.baselineTag} and ${release.releaseTag}.`
                                : 'No change set was recorded for this release.'}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}
